"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sourceSystemDeactivationSchema = exports.sourceSystemUpdateSchema = exports.sourceSystemIdPathSchema = exports.sourceSystemListSchema = exports.sourceSystemSchema = exports.sourceSystemInputSchema = void 0;
// Registering an upstream platform is an INSERT (`RPM-082`, `J-264`).
//
// THE COLUMN IS UNCONSTRAINED ON PURPOSE. `RPM-082`: "Registering a new upstream intake platform is
// an **insert**, not a migration and not a code change. `source_systems` is a table with its own
// lifecycle; there is no enum and no `CHECK`. Nothing downstream branches on the value (D2, N2)."
// So this contract carries the platform's identity as free text and asserts nothing about which
// platforms exist — a `z.enum` here would be the enum the requirement forbids, one layer up.
const zod_1 = require("zod");
exports.sourceSystemInputSchema = zod_1.z
    .strictObject({
    /**
     * The tenant's own name for the platform. Unique among that tenant's live rows
     * (`uq_source_system_code`), and never matched against a list.
     */
    sourceSystemCode: zod_1.z.string().min(1).max(64),
    displayName: zod_1.z.string().min(1).max(200),
    /**
     * The value `carease.facilities.data_source` carries for buildings on this platform, where the
     * platform is mirrored. `varchar(20)` in the table, so the same bound here.
     *
     * THE PLATFORM OPERATOR NAMES IT, when provisioning the tenant (`J-694`, `D-1674`): a tenant's
     * administrator who sends it is refused with `422 MIRROR_VALUE_NAMED_BY_OPERATOR`, and registers
     * a platform with no mirror rows without it. At most ONE live row on the whole platform carries
     * any given value (`uq_source_system_mirror_value`, across tenants since `J-694`), because a
     * building resolves to its platform through exactly this column and a data source is one
     * customer's. Kept in the contract so the refusal says why (`D-1679`).
     */
    mirrorDataSourceValue: zod_1.z.string().min(1).max(20).optional(),
    /** Where the agreement with that platform is written down. Free text; nothing parses it. */
    ingestContractRef: zod_1.z.string().min(1).max(500).optional(),
    /** Whether readings from it carry the oxygen answer `SpO₂`'s bands are scoped to (`RPM-012`). */
    suppliesOxygenStatus: zod_1.z.boolean().optional(),
    /** The day it starts supplying readings. Absent means today. Text as given, like every date here. */
    validFrom: zod_1.z.string().min(1).max(40).optional(),
})
    .meta({ description: "One upstream platform, registered as a row" });
exports.sourceSystemSchema = zod_1.z
    .strictObject({
    sourceSystemId: zod_1.z.uuid(),
    sourceSystemCode: zod_1.z.string(),
    displayName: zod_1.z.string(),
    mirrorDataSourceValue: zod_1.z.string().nullable(),
    ingestContractRef: zod_1.z.string().nullable(),
    suppliesOxygenStatus: zod_1.z.boolean(),
    validFrom: zod_1.z.string(),
    validTo: zod_1.z.string().nullable(),
    /** Set once the platform is deactivated. A deactivated row keeps resolving existing readings. */
    supersededAt: zod_1.z.string().nullable(),
})
    .meta({ id: "SourceSystem", description: "One upstream platform as the register holds it" });
exports.sourceSystemListSchema = zod_1.z
    .strictObject({ sourceSystems: zod_1.z.array(exports.sourceSystemSchema) })
    .meta({
    id: "SourceSystemList",
    description: "Every platform this tenant has registered, newest first",
});
/** One registered platform, by its row (`RPM-092`'s detail, edit and deactivate, `J-296`). */
exports.sourceSystemIdPathSchema = zod_1.z.object({ sourceSystemId: zod_1.z.uuid() });
/**
 * An edit of a registered platform (`RPM-092`, `J-296`): its name, its contract reference and
 * whether it supplies the oxygen answer. The Key (`sourceSystemCode`) and the mirror value are NOT
 * here: a building resolves to its platform through the mirror value, so changing either is a new
 * registration and a deactivation of this one, never an edit that moves buildings silently.
 */
exports.sourceSystemUpdateSchema = zod_1.z
    .strictObject({
    displayName: zod_1.z.string().min(1).max(200).optional(),
    /** `null` clears it. */
    ingestContractRef: zod_1.z.string().min(1).max(500).nullable().optional(),
    suppliesOxygenStatus: zod_1.z.boolean().optional(),
})
    .refine((body) => Object.keys(body).length > 0, {
    message: "Name at least one field to change: display name, contract or oxygen answer.",
})
    .meta({ description: "A change to one registered platform's name, contract or oxygen answer" });
/**
 * A deactivation (`RPM-092`: "Deactivating stops new readings; existing rows keep resolving").
 * The reason is optional and goes on the change trail as given.
 */
exports.sourceSystemDeactivationSchema = zod_1.z
    .strictObject({ statedReason: zod_1.z.string().trim().min(1).max(500).optional() })
    .meta({ description: "Deactivating one platform, with the reason if one is given" });
//# sourceMappingURL=rpm-source-systems.js.map